import apiClient from './client';

export type ConsentType = 'MARKETING' | 'ANALYTICS' | 'DATA_STORAGE';
export type ExportFormat = 'JSON' | 'CSV';

export type ConsentRecord = {
  id: string;
  userId: string;
  consentType: ConsentType;
  version: string;
  accepted: boolean;
  ipAddress?: string;
  userAgent?: string;
  acceptedAt: string;
  createdAt: string;
};

export type PrivacyPreferences = {
  id: string;
  userId: string;
  marketingConsent: boolean;
  analyticsConsent: boolean;
  dataStorageConsent: boolean;
  preferencesVersion: string;
  updatedAt: string;
  createdAt: string;
};

export type GDPRExportRequest = {
  id: string;
  userId: string;
  exportFormat: ExportFormat;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  requestedAt: string;
  completedAt?: string;
  expiresAt?: string;
  downloadUrl?: string;
  fileSizeBytes?: number;
  errorMessage?: string;
  createdAt: string;
};

export type GDPRDeletionRequest = {
  id: string;
  userId: string;
  status: 'PENDING' | 'GRACE_PERIOD' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED';
  requestedAt: string;
  gracePeriodEnd?: string;
  cancelledAt?: string;
  deletedAt?: string;
  reason?: string;
  errorMessage?: string;
  createdAt: string;
};

export type DataInventoryEntry = {
  id: string;
  userId: string;
  piiCategory: string;
  recordCount: number;
  tableName?: string;
  lastScannedAt: string;
  createdAt: string;
};

export async function recordConsent(userId: string, input: { consentType: ConsentType; accepted: boolean; version?: string }) {
  const res = await apiClient.post<ConsentRecord>(`/compliance/privacy/consent/${userId}`, input);
  return res.data;
}

export async function getConsent(userId: string) {
  const res = await apiClient.get<PrivacyPreferences>(`/compliance/privacy/consent/${userId}`);
  return res.data;
}

export async function getConsentHistory(userId: string, limit = 50) {
  const res = await apiClient.get<{ total: number; events: ConsentRecord[] }>(`/compliance/privacy/consent/${userId}/history`, {
    params: { limit },
  });
  return res.data;
}

export async function requestExport(userId: string, format: ExportFormat = 'JSON') {
  const res = await apiClient.post<GDPRExportRequest>(`/compliance/privacy/export/${userId}`, { format });
  return res.data;
}

export async function getExportStatus(userId: string, requestId: string) {
  const res = await apiClient.get<GDPRExportRequest>(`/compliance/privacy/export/${userId}/${requestId}`);
  return res.data;
}

export async function getExportRequests(userId: string) {
  const res = await apiClient.get<{ total: number; requests: GDPRExportRequest[] }>(`/compliance/privacy/export/${userId}`);
  return res.data;
}

export async function requestDeletion(userId: string, reason?: string) {
  const res = await apiClient.post<GDPRDeletionRequest>(`/compliance/privacy/delete/${userId}`, { reason });
  return res.data;
}

export async function getDeletionStatus(userId: string) {
  const res = await apiClient.get<GDPRDeletionRequest>(`/compliance/privacy/delete/${userId}`);
  return res.data;
}

export async function cancelDeletion(userId: string, requestId: string) {
  const res = await apiClient.post<GDPRDeletionRequest>(`/compliance/privacy/delete/${userId}/${requestId}/cancel`);
  return res.data;
}

export async function getInventory(userId: string) {
  const res = await apiClient.get<{ total: number; inventory: DataInventoryEntry[] }>(`/compliance/privacy/inventory/${userId}`);
  return res.data;
}

export async function rescanPii(userId: string) {
  const res = await apiClient.post<{ total: number; inventory: DataInventoryEntry[] }>(`/compliance/privacy/inventory/${userId}/rescan`);
  return res.data;
}
